import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, BarChart3, Target, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { getConsentPreferences } from './consentManager';

export default function CookiePreferencesModal({ isOpen, onClose, onSave }) {
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    if (isOpen) {
      // Charger les préférences existantes
      const current = getConsentPreferences();
      setPreferences(current);
    }
  }, [isOpen]);

  const handleToggle = (category) => {
    if (category === 'necessary') return; // Cannot toggle necessary cookies
    
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSave = () => {
    onSave(preferences);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    setPreferences(allAccepted);
    onSave(allAccepted);
  };

  const handleRejectAll = () => {
    const allRejected = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    setPreferences(allRejected);
    onSave(allRejected);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/50"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-slate-100 p-6 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-slate-900">
              Préférences cookies
            </h2>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            <p className="text-slate-600 text-sm">
              Gérez vos préférences en matière de cookies. Les cookies strictement nécessaires sont toujours 
              activés car ils sont indispensables au fonctionnement du service.
            </p>

            {/* Necessary Cookies */}
            <div className="bg-green-50 rounded-xl p-4 border border-green-200">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3 flex-1">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <Shield className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-slate-900 mb-1">Cookies strictement nécessaires</h3>
                    <p className="text-slate-600 text-sm">
                      Indispensables au fonctionnement du site. Ils permettent l'authentification, la sécurité 
                      et la navigation. Ne peuvent pas être désactivés.
                    </p>
                  </div>
                </div>
                <div className="ml-4 flex items-center gap-2 text-green-600">
                  <Check className="w-5 h-5" />
                  <span className="text-sm font-semibold">Toujours actifs</span>
                </div>
              </div>
              <div className="text-xs text-slate-500 pl-11">
                <strong>Exemples :</strong> Session, authentification, préférences de langue, sécurité
              </div>
            </div>

            {/* Analytics Cookies */}
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3 flex-1">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-slate-900 mb-1">Cookies analytiques / mesure d'audience</h3>
                    <p className="text-slate-600 text-sm">
                      Nous permettent de comprendre comment vous utilisez le service et d'améliorer votre expérience. 
                      Les données sont anonymisées.
                    </p>
                  </div>
                </div>
                <Switch
                  checked={preferences.analytics}
                  onCheckedChange={() => handleToggle('analytics')}
                  className="ml-4"
                />
              </div>
              <div className="text-xs text-slate-500 pl-11">
                <strong>Outils :</strong> Aucun actuellement (réservé à de futurs outils de mesure d'audience)
              </div>
            </div>

            {/* Marketing Cookies */}
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3 flex-1">
                  <div className="bg-purple-100 p-2 rounded-lg">
                    <Target className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-slate-900 mb-1">Cookies marketing / publicitaires</h3>
                    <p className="text-slate-600 text-sm">
                      Permettent de personnaliser les contenus publicitaires et de mesurer l'efficacité de nos 
                      campagnes marketing.
                    </p>
                  </div>
                </div>
                <Switch
                  checked={preferences.marketing}
                  onCheckedChange={() => handleToggle('marketing')}
                  className="ml-4"
                />
              </div>
              <div className="text-xs text-slate-500 pl-11">
                <strong>Outils :</strong> Meta (Facebook) Conversions API (mesure de conversions), et éventuellement d'autres outils publicitaires
              </div>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
              <p className="text-amber-900 text-sm">
                <strong>Impact du refus :</strong> Refuser les cookies analytiques ou marketing n'empêche pas 
                l'accès au service. Vous bénéficierez simplement d'une expérience moins personnalisée.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 p-6 flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleRejectAll}
              variant="outline"
              className="flex-1 border-slate-300"
            >
              Tout refuser
            </Button>
            <Button
              onClick={handleAcceptAll}
              variant="outline"
              className="flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              Tout accepter
            </Button>
            <Button
              onClick={handleSave}
              className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
            >
              Enregistrer mes choix
            </Button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}